import { httpResource } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';

/**
 * One partner area the signed-in user can act in, as returned by
 * `GET /v1/me/partners`. Mirrors the ts-api `PartnerAreaListItem`: `id` is the
 * value the auth layer sends as the `creatorSub` header once selected, `name`
 * is the card title, and `ownerEmail` is the muted sub-label (may be absent).
 */
export interface Partner {
  id: string;
  name: string;
  ownerEmail: string | null;
}

/**
 * The partner areas the caller can act in. Read declaratively through an
 * `httpResource` against the `me`-scoped list endpoint — a normal user gets the
 * areas they hold an active seat in, a superAdmin gets them all (the backend
 * branches on role). Intentionally **not** partner-scoped, so no active partner
 * or `creatorSub` header is needed to load it.
 *
 * Root-provided rather than living with the switcher that first needed it: the
 * "no role" and "access paused" screens also need the area's name and owner
 * email, and the switcher is mounted in the shell on every route, so the list
 * is already loaded by the time either of them renders. A second
 * component-level copy would just duplicate the request.
 *
 * **The better home for this is the membership endpoint.**
 * `GET /v1/me/capabilities` is already partner-scoped and already being called;
 * `partnerName` and `ownerEmail` on that response would let those two screens
 * drop this dependency entirely.
 */
@Injectable({ providedIn: 'root' })
export class PartnersData {
  private readonly api = environment.apiUrl;

  /** The partners the current user can switch between. */
  readonly partners = httpResource<Partner[]>(() => `${this.api}/v1/me/partners`);

  /**
   * One partner by id, once the list has loaded — `undefined` while it is in
   * flight, on error, or for an id the caller has no seat in. Reads signals, so
   * it stays reactive inside a `computed`.
   */
  find(id: string | null | undefined): Partner | undefined {
    return id ? this.partners.value()?.find((partner) => partner.id === id) : undefined;
  }
}
